import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';
import { RouterOutlet } from '@angular/router';
import { ThreeCanvasComponent } from '../../../ctc/canvas/canvas.component';
import { LayoutService } from '../../layout.service';
import { ToolSelectBarComponent } from './commons/components/tool-select-bar/tool-select-bar.component';
import { PartCategoryBarComponent } from './commons/components/part-category-bar/part-category-bar.component';
import { PartGridListComponent } from './commons/components/part-grid-list/part-grid-list.component';
import { WrapToolComponent } from './commons/components/wrap-tool/wrap-tool.component';
import { AiAgentToolComponent } from './commons/components/ai-agent-tool/ai-agent-tool.component';
import { GarageToolComponent } from './commons/components/garage-tool/garage-tool.component';
import { PartListControllerService } from './commons/services/part-list-controller/part-list-controller.service';
import { PartModel, ThreedProject } from './commons/services/project-controller/project-controller';
import { ProjectControllerService } from './commons/services/project-controller/project-controller.service';
import { CapturePreviewDialogComponent } from './commons/components/capture-preview-dialog/capture-preview-dialog.component';
import { BackButtonComponent } from './commons/components/back-button/back-button.component';
import { CartButtonComponent } from './commons/components/cart-button/cart-button.component';
import { SelectedPartListBarComponent } from './commons/components/selected-part-list-bar/selected-part-list-bar.component';
import { CanvasControllerService } from '../../../services/canvas-controller/canvas-controller.service';
import { StickerToolComponent } from './commons/components/sticker-tool/sticker-tool.component';
import { ToolLoadingComponent } from './commons/components/tool-loading/tool-loading.component';
import { MatIconModule } from '@angular/material/icon';
import { ChatControllerService } from './commons/services/chat-controller/chat-controller.service';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';
import { PresetToolComponent } from './commons/components/preset-tool/preset-tool.component';
import { LiveryToolComponent } from './commons/components/livery-tool/livery-tool.component';
import { ScreenControlBarComponent } from './commons/components/screen-control-bar/screen-control-bar.component';

@Component({
  selector: 'workspace-layout',
  imports: [
    CommonModule,
    RouterOutlet,
    ToolSelectBarComponent,
    PartCategoryBarComponent,
    PartGridListComponent,
    WrapToolComponent,
    StickerToolComponent,
    ToolLoadingComponent,
    AiAgentToolComponent,
    GarageToolComponent,
    ScreenControlBarComponent,
    BackButtonComponent,
    CartButtonComponent,
    SelectedPartListBarComponent,
    MatIconModule,
    PresetToolComponent,
    LiveryToolComponent,
  ],
  templateUrl: './workspace-layout.component.html',
  styleUrl: './workspace-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceLayoutComponent implements OnInit, OnDestroy {
  public isLoading: boolean = true;

  public project: ThreedProject | undefined;

  public canvas: ThreeCanvasComponent | undefined;

  public selectedTool: { name: string; icon: string; slug: string } | undefined;
  public selectedPartCategory: string = '';
  public selectedPartCategoryIndex: number = 0;

  public categoryMap: Map<string, Array<PartModel>> = new Map<string, Array<PartModel>>();

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlKey = isMac ? event.metaKey : event.ctrlKey;

    // Ctrl + Z (Undo)
    if (ctrlKey && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.undo();
    }

    // Ctrl + Shift + Z (Redo)
    if (ctrlKey && event.key === 'z' && event.shiftKey) {
      event.preventDefault();
      this.redo();
    }
  }

  constructor(
    public layoutService: LayoutService,
    private threeGraphqlService: ThreeGraphqlService,
    public canvasController: CanvasControllerService,
    public ProjectController: ProjectControllerService,
    private partListController: PartListControllerService,
    private chatController: ChatControllerService,
    private cd: ChangeDetectorRef,
  ) {}

  //---------------------------------------------------------------------------
  // @ Lifecycle hooks
  //---------------------------------------------------------------------------

  ngOnInit(): void {
    this.ProjectController.project$.pipe(takeUntil(this._unsubscribeAll)).subscribe((project) => {
      if (!project) return;
      this.project = project;
      this.cd.markForCheck();
    });

    this.canvasController.canvas$.pipe(takeUntil(this._unsubscribeAll)).subscribe((canvas) => {
      if (!canvas) return;
      this.canvas = canvas;
      this.cd.markForCheck();
    });

    this.layoutService.isLoading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((isLoading) => {
      this.isLoading = isLoading;
      this.cd.markForCheck();
    });
  }
  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
    console.log('destroy');
  }

  //---------------------------------------------------------------------------
  // @ Public methods
  //---------------------------------------------------------------------------

  public chatReset(): void {
    this.chatController.resetChatbot(this.project?.uuid || '').then(() => {});
  }

  public onDragDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (!file) return;
    if (this.selectedTool?.name != 'Sticker') {
      this.layoutService.setDisplaySettimgByToolType('Sticker');
      this.selectedTool = { name: 'Sticker', icon: 'Sticker', slug: 'Sticker' };
    }
    this.selectedPartCategory = '';
    this.selectedPartCategoryIndex = 0;
    this.canvas?.hideSpots();
    this.canvas?.reversePlayAllAnimation();
    this.canvas?.lookAtCenterShpere(true);
    this.layoutService.dragDropFile = file;
  }

  public onToolSelect(toolData: { name: string; slug: string; icon: string } | undefined): void {
    this.layoutService.setDisplaySettimgByToolType(toolData?.slug || '');
    this.selectedPartCategory = '';
    this.selectedPartCategoryIndex = 0;
    if (toolData === this.selectedTool) {
      this.selectedTool = undefined;
    } else this.selectedTool = toolData;

    if (
      this.selectedTool?.name == 'AI' ||
      this.selectedTool?.name == 'Part' ||
      this.selectedTool?.name == 'Wrap' ||
      this.selectedTool?.name == 'Preset'
    )
      this.canvas?.showSpots();
    else {
      this.canvas?.hideSpots();
      this.canvas?.reversePlayAllAnimation();
    }
    this.canvas?.lookAtCenterShpere(true);
  }

  public onClickPartCategory(category: string): void {
    if (!this.project || !this.project.partCategories) return;

    if (this.selectedPartCategory === category) {
      this.canvas?.lookAtCenterShpere(true);

      this.selectedPartCategory = '';
      this.selectedPartCategoryIndex = 0;
      this.layoutService.isBottomEditerBarVisible = false;
      return;
    }

    this.selectedPartCategory = category;
    const selectedPartCategoryIndex =
      this.project?.partCategories?.findIndex((partCategory) => partCategory.title === category) ||
      0;
    this.selectedPartCategoryIndex = selectedPartCategoryIndex;

    if (this.project?.partCategories[selectedPartCategoryIndex].partModels.length == 0) {
      this.threeGraphqlService
        .getPartModels(this.project?.partCategories[selectedPartCategoryIndex])
        .pipe(take(1))
        .subscribe((partModels) => {
          if (!this.project) return;
          if (!this.project.partCategories) return;
          if (!this.project.partCategories[selectedPartCategoryIndex]) return;
          if (!partModels) return;
          this.project.partCategories[selectedPartCategoryIndex].partModels = partModels;
          this.selectedPartCategoryIndex = selectedPartCategoryIndex;
          this.cd.markForCheck();
        });
    } else {
      this.selectedPartCategoryIndex = selectedPartCategoryIndex;
      this.cd.markForCheck();
    }
    this.layoutService.isBottomEditerBarVisible = true;
    const cameraInfo = this.project?.partCategories[this.selectedPartCategoryIndex].cameraInfo;
    if (!cameraInfo) return;
    if (cameraInfo.position && cameraInfo.target)
      this.canvasController.lookAt(cameraInfo.position, cameraInfo.target);
  }

  public onSelectedAddPart(part: PartModel): void {
    if (this.partListController.categoryMap.get(part.category || part.uuid) == part) {
      this.partListController.removePart(part);
      return;
    }
    this.partListController.addPart(part);
  }

  public onSelectedAddCart(part: PartModel): void {}

  public onClickBack(isBack: boolean): void {
    this.layoutService.setDisplaySettimgByToolType('');
    this.selectedPartCategory = '';
    this.selectedPartCategoryIndex = 0;
    this.selectedTool = undefined;
    this.cd.markForCheck();
  }

  private undo(): void {
    const status = this.canvasController.stateManager.undo();
    console.log(status);
    if (!status) return;
    this.canvasController.allMaterialColorInit(true);
    status.deleteInfo?.partModels.forEach((model) => {
      this.canvasController.removeModel(model);
      this.partListController.removePart(model);
    });
    status.addInfo?.partModels.forEach((model) => {
      this.canvasController.addModel(model);
      this.partListController.addPart(model);
    });
    status.setting.forEach((setting) => {
      this.canvasController.materialsColorChange([setting.material], setting.style.value, true);
      this.canvasController.materialsMatteChange([setting.material], setting.isMatte, true);
    });
  }

  private redo(): void {
    const status = this.canvasController.stateManager.redo();
    console.log(status);
    if (!status) return;
    this.canvasController.allMaterialColorInit(true);
    status.deleteInfo?.partModels.forEach((model) => {
      this.canvasController.removeModel(model);
      this.partListController.removePart(model);
    });
    status.addInfo?.partModels.forEach((model) => {
      this.canvasController.addModel(model);
      this.partListController.addPart(model);
    });
    status.setting.forEach((setting) => {
      this.canvasController.materialsColorChange([setting.material], setting.style.value, true);
      this.canvasController.materialsMatteChange([setting.material], setting.isMatte, true);
    });
  }

  //---------------------------------------------------------------------------
  // @ Private methods
  //---------------------------------------------------------------------------
}
